/**
 * Chip binding and mutation for CRM-derived table rows.
 *
 * A "chip" is a TipTap `variable` inline node (see `TiptapEditor/extensions/variables/index.ts`)
 * rendered in a table cell. Stamping SETS/REMOVES instance binding via `objectId`; never appends
 * or bakes values. Idempotent: re-stamping same frame yields byte-identical output; empty frame clears all.
 *
 * Optional `isChipInScope` predicate leaves out-of-scope chips completely untouched. Used by
 * `deriveCrmRows` to keep chips belonging to one array in-scope while preserving chips from other
 * arrays or non-CRM fields.
 *
 * Also clears rendered content on every stamp (resolved value must not persist on binding change).
 * Supports HTML string and JSON TipTap-doc formats.
 *
 * No-op guarantee: returns input unchanged BY REFERENCE if nothing modified (no chip, all chips
 * out-of-scope, or already stamped as requested). Callers comparing by reference (for re-render
 * decisions) can rely on this.
 */

/** chip `template` -> the `objectId` of the CRM instance node that owns that field. */
export type CrmBindingFrame = Readonly<Record<string, string>>;

export const CHIP_NODE_TYPE = 'variable';
export const CHIP_NAME_ATTR = 'data-name';
export const CHIP_TEMPLATE_ATTR = 'data-template';
export const CHIP_OBJECT_ID_ATTR = 'data-object-id';

type TipTapJsonAttrs = Record<string, unknown>;

type TipTapJsonNode = {
  type?: string;
  attrs?: TipTapJsonAttrs;
  content?: TipTapJsonNode[];
  [key: string]: unknown;
};

function tryParseJsonDoc(value: string): TipTapJsonNode | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as TipTapJsonNode;
    }
  } catch {
    // swallow — fallback to HTML parsing handled by caller
  }
  return undefined;
}

function walkJsonNodes(node: TipTapJsonNode | undefined, visit: (node: TipTapJsonNode) => void): void {
  if (!node || typeof node !== 'object') return;
  visit(node);
  node.content?.forEach((child) => walkJsonNodes(child, visit));
}

/** Chip `template`s present in one cell's content, in document order (duplicates included). Empty for non-string / plain-text content. */
export function readChipTemplates(cellContent: unknown): string[] {
  if (typeof cellContent !== 'string') return [];
  // Skip parsing for cells with no chips (cheap optimization; false positives handled below)
  if (!cellContent.includes(CHIP_NODE_TYPE)) return [];

  const jsonDoc = tryParseJsonDoc(cellContent);
  if (jsonDoc) {
    const templates: string[] = [];
    walkJsonNodes(jsonDoc, (node) => {
      if (node.type === CHIP_NODE_TYPE && typeof node.attrs?.template === 'string') {
        templates.push(node.attrs.template);
      }
    });
    return templates;
  }
  const doc = new DOMParser().parseFromString(cellContent, 'text/html');
  return Array.from(doc.querySelectorAll(`span[data-type="${CHIP_NODE_TYPE}"]`))
    .map((el) => el.getAttribute(CHIP_TEMPLATE_ATTR))
    .filter((template): template is string => !!template);
}

/**
 * Set/remove chip bindings per frame; clear resolved content. Scoped by isChipInScope (all in-scope by default).
 * Returns input unchanged by reference if nothing modified.
 */
export function stampChipBindings(cellContent: string, frame: CrmBindingFrame, isChipInScope?: (chipName: string) => boolean): string;
export function stampChipBindings(cellContent: unknown, frame: CrmBindingFrame, isChipInScope?: (chipName: string) => boolean): unknown;
export function stampChipBindings(
  cellContent: unknown,
  frame: CrmBindingFrame,
  isChipInScope: (chipName: string) => boolean = () => true
): unknown {
  if (typeof cellContent !== 'string') return cellContent;

  // Skip parsing for cells with no chips (cheap optimization; false positives handled below)
  if (!cellContent.includes(CHIP_NODE_TYPE)) return cellContent;

  const jsonDoc = tryParseJsonDoc(cellContent);
  if (jsonDoc) {
    let changed = false;
    walkJsonNodes(jsonDoc, (node) => {
      if (node.type !== CHIP_NODE_TYPE) return;
      const attrs = node.attrs;
      if (!attrs || typeof attrs.template !== 'string') return;
      if (!isChipInScope(attrs.template)) return;

      const objectId = frame[attrs.template];
      if (objectId === undefined) {
        if (attrs.objectId !== undefined) {
          delete attrs.objectId;
          changed = true;
        }
      } else if (attrs.objectId !== objectId) {
        attrs.objectId = objectId;
        changed = true;
      }
      // Strip content on every stamp (defensive—prevent stale resolved values)
      if (node.content !== undefined) {
        delete node.content;
        changed = true;
      }
    });
    return changed ? JSON.stringify(jsonDoc) : cellContent;
  }

  const templateElement = document.createElement('template');
  templateElement.innerHTML = cellContent;
  let changed = false;
  templateElement.content.querySelectorAll(`span[data-type="${CHIP_NODE_TYPE}"]`).forEach((el) => {
    const chipTemplate = el.getAttribute(CHIP_TEMPLATE_ATTR);
    if (!chipTemplate) return;
    if (!isChipInScope(chipTemplate)) return;

    const objectId = frame[chipTemplate];
    if (objectId === undefined) {
      if (el.hasAttribute(CHIP_OBJECT_ID_ATTR)) {
        el.removeAttribute(CHIP_OBJECT_ID_ATTR);
        changed = true;
      }
    } else if (el.getAttribute(CHIP_OBJECT_ID_ATTR) !== objectId) {
      el.setAttribute(CHIP_OBJECT_ID_ATTR, objectId);
      changed = true;
    }

    if (el.textContent !== '') {
      el.textContent = '';
      changed = true;
    }
  });

  return changed ? templateElement.innerHTML : cellContent;
}
